import styles from "./navigation.module.css";
import { Flex } from "@radix-ui/themes";
import { useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "~/components/RouterLink";
import { LinkCta } from "~/components/LinkCta";
import { Phone } from "~/components/icons/Phone";
import { useContext, useState } from "react";
import {
  Locale,
  LocalizationContext,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { setLocalePreference } from "~/components/localization/locale.functions";
import { Sidebar } from "~/components/Sidebar";

type Localization = {
  freeConsult: string;
};

const englishLocalization: Localization = {
  freeConsult: "Free consult",
};

const spanishLocalization: Localization = {
  freeConsult: "Consulta gratuita",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function Navigation() {
  const router = useRouter();
  const persistLocale = useServerFn(setLocalePreference);
  const { locale, setLocale } = useContext(LocalizationContext);
  const [pendingLocale, setPendingLocale] = useState<Locale | null>(null);
  const getLocalization = useLocalization(localized);

  const handleLocaleChange = async (nextLocale: Locale) => {
    if (nextLocale === locale || pendingLocale) {
      return;
    }

    setPendingLocale(nextLocale);

    try {
      const { locale: persistedLocale } = await persistLocale({
        data: { locale: nextLocale },
      });
      setLocale(persistedLocale);
      await router.invalidate();
    } finally {
      setPendingLocale(null);
    }
  };

  return (
    <Flex
      width="100dvw"
      direction="column"
      className={styles.navbar}
      position="fixed"
    >
        <Flex
          width="100dvw"
          justify="end"
          gap="2"
          className={styles.languageBar}
          px="4"
        >
          <button
            type="button"
            onClick={() => handleLocaleChange(Locale.EN)}
            className={styles.languageOption}
            aria-pressed={locale === Locale.EN}
            disabled={pendingLocale !== null}
          >
            <Flex gap="1" align="center">
              <img src="/us.svg" alt="English" width="16px" height="12px" />
              English
            </Flex>
          </button>
          <button
            type="button"
            onClick={() => handleLocaleChange(Locale.ES)}
            className={styles.languageOption}
            aria-pressed={locale === Locale.ES}
            disabled={pendingLocale !== null}
          >
            <Flex gap="1" align="center">
              <img src="/es.svg" alt="Español" width="16px" height="12px" />
              Español
            </Flex>
          </button>
        </Flex>
        <div className={styles.headerBackdrop} aria-hidden="true" />
        <Flex width="100dvw" justify="between" className={styles.header} asChild>
          <header>
            <Link to="/" className={styles.logoLink}>
              <img
                className={styles.logo}
                src="/optimized/miami-ortho-logo-180.webp"
                srcSet="/optimized/miami-ortho-logo-180.webp 180w, /optimized/miami-ortho-logo-350.webp 350w"
                sizes="180px"
                width="180"
                height="72"
                alt="logo of miami orthodontic specialists"
              />
            </Link>
            <Flex align="center">
              <Flex
                display={{ initial: "none", sm: "flex" }}
                gap="1"
                align="center"
                height="100%"
                px="7"
                asChild
              >
                <Link to="tel:305-407-9000" className={styles.phoneCta}>
                  <Phone size={20} />
                  305-407-9000
                </Link>
              </Flex>
              <Flex
                display={{ initial: "none", sm: "flex" }}
                align="center"
                height="100%"
                className={styles.consultCta}
              >
                <LinkCta to="/free-consult" px="7">
                  {getLocalization("freeConsult")}
                </LinkCta>
              </Flex>
              <Sidebar />
            </Flex>
          </header>
        </Flex>
      </Flex>
  );
}
